import { useFormik } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { recoverSchema } from './RecoverEmailSchema';
import { useAuthValue } from '../../context/AuthContext';
import { isFleetcare, isMindy, isOms } from '../../utils';

/* eslint-disable-next-line */
export interface RecoverPassFormProps {
    handleSuccess(): void;
}

export function RecoverPassForm(props: RecoverPassFormProps) {
    const { t } = useTranslation();
    const context = useAuthValue();

    const formik = useFormik({
        initialValues: { email: '' },
        validationSchema: recoverSchema,
        validateOnMount: true,
        onSubmit: async (values: { email: string }) => {
            await context?.resetPassword(values.email);
            props.handleSuccess();
        },
    });
    if (isMindy || isFleetcare || isOms) {
        return (
            <div className="w-full">
                {/* Title and description */}
                <div className="mb-6">
                    <h2 className="text-2xl font-poppins font-semibold text-white mb-1">{t('recover.title')}</h2>
                    <p className="text-sm text-gray-400 font-roboto">{t('recover.description')}</p>
                    <div className="mt-4 border-t border-gray-900"></div>
                </div>

                <form onSubmit={formik.handleSubmit} className="space-y-5">
                    {/* Email field */}
                    <div>
                        <label htmlFor="recoverEmail" className="text-white text-sm mb-1.5 block font-poppins">
                            {t('recover.label')}
                        </label>
                        <input
                            id="recoverEmail"
                            name="email"
                            type="email"
                            placeholder={t('recover.emailPlaceholder')}
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`w-full px-3 py-2 rounded-md border text-white placeholder:text-gray-500 focus:outline-none focus:border-primary font-roboto disabled:bg-gray-700 disabled:border-gray-600 disabled:text-gray-500 ${
                                formik.touched.email && formik.errors.email
                                    ? 'border-red-500 bg-gray-900'
                                    : 'bg-gray-900 border-gray-700'
                            }`}
                        />
                        <AnimatePresence>
                            {formik.touched.email && formik.errors.email && (
                                <motion.p
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="mt-1 text-sm text-red-400"
                                >
                                    {t(formik.errors.email)}
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={formik.isSubmitting || !formik.isValid}
                        className="w-full bg-primary-500 hover:bg-primary-400 disabled:opacity-50 text-white font-poppins font-semibold py-3 rounded-lg transition-colors"
                    >
                        {t('recover.button')}
                    </button>

                    {/* Back to login */}
                    <p className="text-sm text-gray-400 font-roboto text-center">
                        <Link
                            to="/login"
                            className="text-primary-200 hover:text-primary-300 underline transition-colors"
                        >
                            {t('recover.backToLogin')}
                        </Link>
                    </p>
                </form>
            </div>
        );
    }

    return (
        <div className="bg-white xs:w-1/2 xs:shadow-xl shadow-slate-500/50 px-14 py-10 rounded-md font-['Roboto'] flex flex-col justify-center items-start max-w-[480px]">
            <form onSubmit={formik.handleSubmit}>
                <h3 className="text-3xl font-bold mb-2">{t('recover.title')}</h3>
                <p className="text-base w-full">{t('recover.description')}</p>
                <div className="my-5 w-full">
                    <label className="mb-2 block text-sm" htmlFor="email">
                        {t('recover.label')}
                    </label>
                    <input
                        placeholder={t('recover.label')}
                        name="email"
                        type="email"
                        onChange={formik.handleChange}
                        value={formik.values.email}
                        onBlur={formik.handleBlur}
                        className={`w-full rounded-md h-[48px] text-lg border p-2 border-gray-400 hover:border-blue-600 focus:border-blue-600 outline-none bg-white ${
                            formik.touched.email &&
                            formik.errors.email &&
                            'border-red-500 focus:border-red-500 hover:border-red-700'
                        }`}
                    />
                    {formik.touched.email && formik.errors.email && (
                        <p className="mt-1 text-sm text-red-500 font-semibold">{t(formik.errors.email)}</p>
                    )}
                </div>
                <button
                    className="w-full h-[50px] bg-blue-800 disabled:bg-blue-200 rounded-md text-white font-['Poppins']"
                    type="submit"
                    color="blue"
                    disabled={formik.isSubmitting || !formik.isValid}
                >
                    {t('recover.button')}
                </button>
            </form>
        </div>
    );
}

export default RecoverPassForm;
